// Sprint UI-6 Phase B → rate-limit-banner-ssot — Network banner.
//
// Renders at the very top of the page (above/inside Topbar slot) when the
// app can't talk to the server. Only one banner shows at a time:
//
//   1. disconnected  → red:   "Disconnected — agents keep running…" + Retry
//   2. reconnecting  → amber: "Reconnecting" + spinner
//
// All copy via i18n (Layer 7 §4.3.2).
//
// Sprint topbar-banners-retire (2026-09-11, founder directive) — the
// rate-limit and memory-constrained strips are retired: capacity lives in
// `RunnersHealthPill` → `UsageModal`, Settings → Servers, and the
// Composer's inline notice. `disconnected` / `reconnecting` survive
// because in that state the app is otherwise mute and Retry is the only
// action that exists.

import { useAppStore } from '../../stores/app.store';
import { getSocket } from '../../lib/socket';
import { useTranslation } from '../../i18n';
import { Spinner } from '../atoms/Spinner';

export function NetworkBanner() {
  const connectionStatus = useAppStore((s) => s.connectionStatus);

  if (connectionStatus === 'disconnected') return <DisconnectedBanner />;
  if (connectionStatus === 'reconnecting') return <ReconnectingBanner />;

  return null;
}

function ReconnectingBanner() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 px-4 py-1.5 pt-safe-top box-content bg-status-warning/15 text-status-warning text-body border-b border-status-warning/30"
      data-testid="network-banner-reconnecting"
    >
      <Spinner size="sm" />
      <span>{t('networkBanner.reconnecting')}</span>
    </div>
  );
}

function DisconnectedBanner() {
  const { t } = useTranslation();

  function handleRetry() {
    try {
      getSocket().connect();
    } catch {
      // singleton may not exist; ignore
    }
  }

  return (
    <div
      role="alert"
      className="flex items-center justify-center gap-3 px-4 py-1.5 pt-safe-top box-content bg-status-error/15 text-body border-b border-status-error/30"
      data-testid="network-banner-disconnected"
    >
      <span className="text-foreground">{t('networkBanner.disconnected')}</span>
      <button
        type="button"
        onClick={handleRetry}
        className="px-3 py-1 rounded-md bg-status-error/20 text-status-error hover:bg-status-error/30 text-caption font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-status-error/40"
        data-testid="network-banner-retry"
      >
        {t('networkBanner.disconnectedRetry')}
      </button>
    </div>
  );
}
